import { convertRowDataToFieldsForScript } from '@/common/utils/rowDataUtils'
import { resolveListboxCodeFromPageFields } from '@/common/utils/listboxTableResolve'
import {
  formatTimezonelessDateForDisplay,
  isTimezonelessDateWire,
} from '@/common/utils/timezonelessDate'

export type ColumnFormatContext = {
  pageFields?: Record<string, any> | null
  tableFieldName?: string
  columnFieldName?: string
  columnElementId?: string
}

const SUBTABLE_SEPARATOR = '; '

const unwrapTyped = (input: any): any => {
  if (input === null || input === undefined) return input

  if (typeof input === 'object') {
    if ('value' in input) {
      return (input as any).value
    }
    if ('_text' in input) {
      return (input as any)._text
    }
  }

  return input
}

const stringifyScalar = (value: any): string => {
  const unwrapped = unwrapTyped(value)
  if (unwrapped === null || unwrapped === undefined) return ''
  if (typeof unwrapped === 'string') return unwrapped.trim()
  if (typeof unwrapped === 'number' || typeof unwrapped === 'boolean') {
    return String(unwrapped)
  }
  return ''
}

const looksLikeDisplayText = (value: string): boolean => {
  const normalized = value.trim()
  if (!normalized) return false
  return !/^\d+$/.test(normalized)
}

const scoreDisplayCandidate = (value: string): number => {
  const normalized = value.trim()
  if (!normalized) return Number.NEGATIVE_INFINITY

  let score = 0

  if (!/^\d+$/.test(normalized)) score += 20
  if (/[A-Za-zА-Яа-яЁё]/.test(normalized)) score += 20
  if (!normalized.includes('\n') && !normalized.includes('\r')) score += 10
  if (normalized.length <= 120) score += 10
  if (/\s/.test(normalized)) score += 5

  if (normalized.includes('\n') || normalized.includes('\r')) score -= 30
  if (/[{};]/.test(normalized)) score -= 15
  if (/\bfunction\b|\bvar\b|\bif\s*\(|=>/.test(normalized)) score -= 20
  if (normalized.length > 200) score -= 20

  return score
}

export const extractUniDataRowColumnValue = (row: any, field: string): any => {
  return extractFieldValueInternal(row, field, new Set<any>())
}

const extractFieldValue = (row: any, field: string): any => {
  return extractUniDataRowColumnValue(row, field)
}

const extractFieldValueInternal = (row: any, field: string, visited: Set<any>): any => {
  if (!row || !field) return undefined

  const normalizedField = field.trim()
  if (!normalizedField) return undefined
  if (typeof row === 'object' && row !== null) {
    if (visited.has(row)) return undefined
    visited.add(row)
  }
  const dottedSuffix = `.${normalizedField}`

  const flattenedFields = convertRowDataToFieldsForScript(row)
  if (flattenedFields && typeof flattenedFields === 'object') {
    const exact = flattenedFields[normalizedField]
    const exactValue = exact?.value ?? exact
    if (exactValue !== undefined && exactValue !== null && String(exactValue).trim() !== '') {
      return exactValue
    }

    for (const [key, value] of Object.entries(flattenedFields)) {
      if (key === normalizedField || key.endsWith(dottedSuffix)) {
        const normalizedValue = (value as any)?.value ?? value
        if (
          normalizedValue !== undefined &&
          normalizedValue !== null &&
          String(normalizedValue).trim() !== ''
        ) {
          return normalizedValue
        }
      }
    }
  }

  const readUniDataLikeField = (candidateFieldName: string): any => {
    if (!row || typeof row !== 'object') return undefined

    if (typeof (row as any).getFieldAsObject === 'function') {
      const value = (row as any).getFieldAsObject(candidateFieldName)
      if (value !== undefined && value !== null) {
        return value
      }
    }

    if (typeof (row as any).getField === 'function') {
      const typed = (row as any).getField(candidateFieldName)
      const unwrapped = unwrapTyped(typed)
      if (unwrapped !== undefined && unwrapped !== null) {
        return unwrapped
      }
    }

    return undefined
  }

  const uniDataLikeExact = readUniDataLikeField(normalizedField)
  if (uniDataLikeExact !== undefined && uniDataLikeExact !== null) {
    return uniDataLikeExact
  }

  const uniDataLikeFields =
    typeof (row as any)?._getAllFields === 'function'
      ? (row as any)._getAllFields()
      : (row as any)?.fields

  if (uniDataLikeFields && typeof uniDataLikeFields === 'object' && !Array.isArray(uniDataLikeFields)) {
    for (const key of Object.keys(uniDataLikeFields)) {
      if (key === normalizedField || key.endsWith(dottedSuffix)) {
        const value =
          readUniDataLikeField(key) ??
          unwrapTyped((uniDataLikeFields as Record<string, any>)[key])
        if (value !== undefined && value !== null) {
          if (typeof value === 'string' && value.trim() === '') continue
          return value
        }
      }
    }
  }

  const direct = (row as any)[normalizedField]
  if (direct !== undefined) {
    const unwrapped = unwrapTyped(direct)
    if (unwrapped !== undefined && unwrapped !== null) {
      return unwrapped
    }
  }

  const fieldsContainer = (row as any)?.fields
  if (fieldsContainer && typeof fieldsContainer === 'object' && !Array.isArray(fieldsContainer)) {
    if (normalizedField in fieldsContainer) {
      const fieldData = fieldsContainer[normalizedField]
      const unwrapped = unwrapTyped(fieldData)
      if (unwrapped !== undefined && unwrapped !== null) {
        return unwrapped
      }
    }
    for (const key of Object.keys(fieldsContainer)) {
      if (key === normalizedField || key.endsWith(dottedSuffix)) {
        const unwrapped = unwrapTyped(fieldsContainer[key])
        if (unwrapped !== undefined && unwrapped !== null) {
          if (typeof unwrapped === 'string' && unwrapped.trim() === '') continue
          return unwrapped
        }
      }
    }
  }

  if (row && typeof row === 'object' && !Array.isArray(row)) {
    for (const key of Object.keys(row)) {
      if (key === 'fields') continue
      if (key === normalizedField || key.endsWith(dottedSuffix)) {
        const unwrapped = unwrapTyped((row as any)[key])
        if (unwrapped !== undefined && unwrapped !== null) {
          if (typeof unwrapped === 'string' && unwrapped.trim() === '') continue
          return unwrapped
        }
      }
    }
  }

  const nestedCandidates: any[] = []
  const pushNestedCandidate = (candidate: any) => {
    const unwrapped = unwrapTyped(candidate)
    if (
      unwrapped &&
      typeof unwrapped === 'object' &&
      !Array.isArray(unwrapped) &&
      Array.isArray((unwrapped as any).rows) &&
      (unwrapped as any).rows.length === 1
    ) {
      nestedCandidates.push((unwrapped as any).rows[0])
    }
  }

  if (uniDataLikeFields && typeof uniDataLikeFields === 'object' && !Array.isArray(uniDataLikeFields)) {
    Object.values(uniDataLikeFields).forEach(pushNestedCandidate)
  }

  if (fieldsContainer && typeof fieldsContainer === 'object' && !Array.isArray(fieldsContainer)) {
    Object.values(fieldsContainer).forEach(pushNestedCandidate)
  }

  if (row && typeof row === 'object' && !Array.isArray(row)) {
    Object.entries(row).forEach(([key, value]) => {
      if (key === 'fields') return
      pushNestedCandidate(value)
    })
  }

  for (const nestedRow of nestedCandidates) {
    const nestedValue = extractFieldValueInternal(nestedRow, normalizedField, visited)
    if (nestedValue !== undefined && nestedValue !== null) {
      return nestedValue
    }
  }

  return undefined
}

export const resolveUniDataRowLabel = (row: any): string => {
  if (row == null) return ''
  if (typeof row !== 'object' || Array.isArray(row)) {
    return String(row).trim()
  }

  if (row.type === 'UniDataTable' && row.value && typeof row.value === 'object' && Array.isArray(row.value.rows)) {
    for (const nestedRow of row.value.rows) {
      const nestedLabel = resolveUniDataRowLabel(nestedRow)
      if (nestedLabel) return nestedLabel
    }
  }

  const candidates: string[] = []

  const pushCandidate = (value: any) => {
    const resolved = stringifyScalar(value)
    if (resolved) candidates.push(resolved)
  }

  const allFields =
    typeof (row as any)?._getAllFields === 'function'
      ? (row as any)._getAllFields()
      : null

  if (allFields && typeof allFields === 'object' && !Array.isArray(allFields)) {
    for (const [key, value] of Object.entries(allFields)) {
      if (key.startsWith('_')) continue
      pushCandidate(value)
    }
  }

  const fields = row?.fields
  if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
    for (const [key, value] of Object.entries(fields)) {
      if (key.startsWith('_')) continue
      pushCandidate(value)
    }
  }

  for (const [key, value] of Object.entries(row)) {
    if (key === 'fields' || key.startsWith('_')) continue
    pushCandidate(value)
  }

  const uniqueCandidates = Array.from(new Set(candidates))
  const bestCandidate = uniqueCandidates
    .map((candidate) => ({ candidate, score: scoreDisplayCandidate(candidate) }))
    .sort((left, right) => right.score - left.score)[0]?.candidate

  return bestCandidate || uniqueCandidates.find(looksLikeDisplayText) || uniqueCandidates[0] || ''
}

export const resolveUniDataRowKey = (row: any): string => {
  if (row == null) return ''
  if (typeof row !== 'object' || Array.isArray(row)) {
    return String(row)
  }

  const id = stringifyScalar(extractFieldValue(row, '_id') ?? extractFieldValue(row, 'id'))
  const typeId = stringifyScalar(extractFieldValue(row, '_typeId') ?? extractFieldValue(row, 'typeId'))
  const versionId = stringifyScalar(extractFieldValue(row, '_versionId') ?? extractFieldValue(row, 'versionId'))

  if (id) {
    return ['row', typeId || 'unknown', id, versionId || ''].filter(Boolean).join(':')
  }

  const code = stringifyScalar(extractFieldValue(row, 'code'))
  if (code) {
    return `code:${code}`
  }

  const kind = stringifyScalar(extractFieldValue(row, 'kind'))
  const value = stringifyScalar(extractFieldValue(row, 'value'))
  if (kind || value) {
    return ['kind', kind || '', value || ''].filter(Boolean).join(':')
  }

  const fields = row?.fields
  if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
    const fieldNames = Object.keys(fields)
      .filter((key) => !key.startsWith('_'))
      .sort()
      .slice(0, 5)
    if (fieldNames.length) {
      return ['fields', typeId || '', ...fieldNames].filter(Boolean).join(':')
    }
  }

  return typeId ? `row:${typeId}` : ''
}

const extractRows = (value: any): any[] => {
  if (value == null || value === '') return []

  if (Array.isArray(value)) {
    return value
  }

  if (typeof value === 'string') {
    const t = value.trim()
    if ((t.startsWith('{') || t.startsWith('[')) && t.length > 1) {
      try {
        return extractRows(JSON.parse(t))
      } catch {
        return []
      }
    }
    return []
  }

  if (value && typeof value === 'object') {
    const directRows = (value as any).rows
    if (Array.isArray(directRows)) {
      return directRows
    }

    const nestedValue = (value as any).value
    if (nestedValue != null && nestedValue !== '') {
      return extractRows(nestedValue)
    }
  }

  return []
}

/** Скаляр code из typed value ячейки (не UniDataTable с rows). */
const peelScalarListBoxCode = (value: any): unknown => {
  if (value == null || value === '') return undefined
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) return undefined
  if (Array.isArray((value as any).rows)) return undefined

  const nested = (value as any).value
  if (nested != null && typeof nested === 'object' && Array.isArray((nested as any).rows)) {
    return undefined
  }
  if (Object.prototype.hasOwnProperty.call(value, 'value') && nested !== undefined) {
    return nested
  }
  return undefined
}

const formatSubTable = (value: any, fieldName: string): string => {
  const rows = extractRows(value)
  if (!rows.length) return ''

  return rows
    .map(row => {
      const cell = extractFieldValue(row, fieldName)
      return cell !== undefined && cell !== null ? String(cell).trim() : ''
    })
    .filter(Boolean)
    .join(SUBTABLE_SEPARATOR)
}

const resolveSubTableListBoxLabel = (
  code: unknown,
  defaultValue: string,
  context?: ColumnFormatContext,
): string => {
  if (code === undefined || code === null || String(code).trim() === '') {
    return defaultValue
  }

  const columnFieldName = context?.columnFieldName?.trim()
  if (context?.pageFields && columnFieldName) {
    const text = resolveListboxCodeFromPageFields(
      context.pageFields,
      columnFieldName,
      code,
      {
        tableFieldName: context.tableFieldName,
        columnElementId: context.columnElementId,
      },
    )
    if (text) return text
  }

  return defaultValue
}

const formatSubTableListBox = (
  value: any,
  codeFieldName: string,
  defaultValue: string,
  context?: ColumnFormatContext,
): string => {
  const rows = extractRows(value)
  if (!rows.length) {
    const scalarCode = peelScalarListBoxCode(value)
    if (scalarCode !== undefined && scalarCode !== null && String(scalarCode).trim() !== '') {
      return resolveSubTableListBoxLabel(scalarCode, defaultValue, context)
    }
    return defaultValue
  }

  const rendered = rows
    .map(row => {
      if (codeFieldName) {
        const code = extractFieldValue(row, codeFieldName)
        return resolveSubTableListBoxLabel(code, defaultValue, context)
      }

      const description =
        extractFieldValue(row, 'description') ??
        extractFieldValue(row, 'name') ??
        extractFieldValue(row, 'title')
      return description !== undefined && description !== null ? String(description).trim() : ''
    })
    .filter(Boolean)

  if (!rendered.length) {
    return defaultValue
  }

  return rendered.join(SUBTABLE_SEPARATOR)
}

// Helper to parse date and extract components
const parseDate = (value: any): { day: number; month: number; year: number } | null => {
  if (!value) return null

  let date: Date | null = null

  // Try to parse as Date object
  if (value instanceof Date) {
    date = value
  } else if (typeof value === 'string') {
    // Try parsing common date formats
    const parsed = new Date(value)
    if (!isNaN(parsed.getTime())) {
      date = parsed
    }
  } else if (typeof value === 'number') {
    date = new Date(value > 1000000000000 ? value : value * 1000)
  }

  if (!date || isNaN(date.getTime())) return null

  return {
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear()
  }
}

const parseDateObject = (value: any): Date | null => {
  if (!value) return null

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number') {
    const date = new Date(value > 1000000000000 ? value : value * 1000)
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed === 'nullDateGMT' || trimmed === 'nullDateISO') {
      return null
    }

    const direct = new Date(trimmed)
    if (!Number.isNaN(direct.getTime())) {
      return direct
    }

    const ruDateTimeMatch = trimmed.match(
      /^(\d{2})\.(\d{2})\.(\d{2,4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/,
    )
    if (ruDateTimeMatch) {
      const [, day, month, year, hours = '00', minutes = '00', seconds = '00'] = ruDateTimeMatch
      const normalizedYear = year.length === 2 ? `20${year}` : year
      const parsed = new Date(
        Number(normalizedYear),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds),
      )
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }

    const timeOnlyMatch = trimmed.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/)
    if (timeOnlyMatch) {
      const [, hours, minutes, seconds = '00'] = timeOnlyMatch
      const parsed = new Date(1970, 0, 1, Number(hours), Number(minutes), Number(seconds))
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
  }

  return null
}

const hasDateMaskTokens = (format: string): boolean =>
  /yyyy|yy|MM|dd|HH|hh|mm|ss/.test(format)

const DATE_LIKE_COLUMN_TYPES = new Set(['DATE', 'DATETIME', 'TIMESTAMP', 'TIME'])

export function normalizeTableColumnType(type: unknown): string {
  if (!type) return ''
  if (typeof type === 'string') return type.trim().toUpperCase()
  if (typeof type === 'object' && type !== null) {
    const obj = type as Record<string, unknown>
    if (typeof obj.value === 'string') return obj.value.trim().toUpperCase()
    if (typeof obj._text === 'string') return obj._text.trim().toUpperCase()
  }
  return String(type).trim().toUpperCase()
}

export function isDateLikeTableColumnType(type: unknown): boolean {
  return DATE_LIKE_COLUMN_TYPES.has(normalizeTableColumnType(type))
}

/** Swing DateTimeFormat defaults: Date → dd.MM.yyyy, DateTime → dd.MM.yyyy HH:mm:ss */
export function defaultSwingDateMaskForColumnType(type: unknown): string | null {
  const normalized = normalizeTableColumnType(type)
  if (normalized === 'DATE') return 'dd.MM.yyyy'
  if (normalized === 'DATETIME' || normalized === 'TIMESTAMP') return 'dd.MM.yyyy HH:mm:ss'
  if (normalized === 'TIME') return 'HH:mm'
  return null
}

export function tableColumnTypeUsesTimeInDisplay(type: unknown): boolean {
  const normalized = normalizeTableColumnType(type)
  return normalized === 'DATETIME' || normalized === 'TIMESTAMP' || normalized === 'TIME'
}

const formatDateByMask = (value: any, format: string): string | null => {
  if (!hasDateMaskTokens(format)) return null

  const date = parseDateObject(value)
  if (!date) return null

  const replacements: Record<string, string> = {
    yyyy: String(date.getFullYear()),
    yy: String(date.getFullYear()).slice(-2),
    MM: String(date.getMonth() + 1).padStart(2, '0'),
    dd: String(date.getDate()).padStart(2, '0'),
    HH: String(date.getHours()).padStart(2, '0'),
    hh: String(date.getHours()).padStart(2, '0'),
    mm: String(date.getMinutes()).padStart(2, '0'),
    ss: String(date.getSeconds()).padStart(2, '0'),
  }

  return format.replace(/yyyy|yy|MM|dd|HH|hh|mm|ss/g, (token) => replacements[token] ?? token)
}

/**
 * Formats a single row/item using a format template string
 * Supports patterns like: %${fieldName}s, %${fieldName}d, %${fieldName}td, %${fieldName}tm, %${fieldName}ty
 */
export const formatItem = (row: any, template: string): string => {
  if (!template || !row) return ''

  // Pattern for %${fieldName}s, %${fieldName}d, %${fieldName}td, %${fieldName}tm, %${fieldName}ty
  const PLACEHOLDER_REGEXP = /%\$\{([^}]+)\}(td|tm|ty|s|d)/g

  // Check if template uses %${fieldName} syntax
  if (template.includes('%${')) {
    return template.replace(PLACEHOLDER_REGEXP, (match, fieldName, suffix) => {
      const valueFromRow = extractFieldValue(row, fieldName)

      if (valueFromRow === undefined || valueFromRow === null) {
        return ''
      }

      // Handle date suffixes: td (day), tm (month), ty (year)
      if (suffix === 'td' || suffix === 'tm' || suffix === 'ty') {
        const dateParts = parseDate(valueFromRow)
        if (!dateParts) return ''

        if (suffix === 'td') {
          return String(dateParts.day).padStart(2, '0')
        } else if (suffix === 'tm') {
          return String(dateParts.month).padStart(2, '0')
        } else if (suffix === 'ty') {
          return String(dateParts.year)
        }
      }

      // Handle numeric suffix: d
      if (suffix === 'd') {
        return String(valueFromRow)
      }

      // Handle string suffix: s
      if (suffix === 's') {
        return String(valueFromRow)
      }

      // Default: just return the value as string
      return String(valueFromRow)
    }).trim()
  }

  // Fallback to ${fieldName} syntax (without %)
  if (template.includes('${')) {
    const FALLBACK_REGEXP = /\$\{([^}]+)\}/g
    return template.replace(FALLBACK_REGEXP, (_, token) => {
      const valueFromRow = extractFieldValue(row, token)
      return valueFromRow !== undefined && valueFromRow !== null ? String(valueFromRow) : ''
    }).trim()
  }

  // If no placeholders, try to use template as field name
  const fallback = extractFieldValue(row, template)
  return fallback !== undefined && fallback !== null ? String(fallback).trim() : ''
}

const formatSubTableExt = (value: any, template: string): string => {
  const rows = extractRows(value)
  if (!rows.length) return ''

  return rows
    .map(row => formatItem(row, template))
    .filter(Boolean)
    .join(SUBTABLE_SEPARATOR)
}

const formatFileSize = (value: any): string | null => {
  const raw = typeof value === 'object' && value ? (value as any).value ?? value : value
  const numeric = typeof raw === 'string' ? Number(raw.replace(/[^0-9.-]/g, '').trim()) : Number(raw)

  if (!Number.isFinite(numeric)) {
    return null
  }

  if (numeric === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const exponent = Math.min(Math.floor(Math.log(Math.abs(numeric)) / Math.log(1024)), units.length - 1)
  const human = numeric / Math.pow(1024, exponent)

  return `${human.toFixed(human >= 10 || human % 1 === 0 ? 0 : 2)} ${units[exponent]}`
}

const formatMoney = (value: any): string | null => {
  const raw = typeof value === 'object' && value ? (value as any).value ?? value : value
  const numeric = typeof raw === 'string'
    ? Number(raw.replace(/\s/g, '').replace(',', '.'))
    : Number(raw)

  if (!Number.isFinite(numeric)) {
    return null
  }

  return numeric.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

interface IconItem {
  code?: any
  description: string
  icon?: string
  tooltip?: string
  originalValue?: any
}

const formatIconSubTable = (value: any, params: {
  name: string
  code?: string
  image?: string
  tooltip?: string
  orderBy?: string
}): IconItem[] => {
  const rows = extractRows(value)
  if (!rows.length) return []

  const {
    name: fieldName,
    code: fieldCode = '_id',
    image: fieldImage = 'icon',
    tooltip: fieldTooltip = 'description',
    orderBy: fieldOrder
  } = params

  // Filter and sort rows
  let processedRows = rows

  // Filter out hidden rows (negative order only when the field is present)
  if (fieldOrder) {
    processedRows = processedRows.filter((row: any) => {
      const orderValue = extractFieldValue(row, fieldOrder)
      if (orderValue === undefined || orderValue === null) {
        return true
      }
      const numValue = typeof orderValue === 'number' ? orderValue : Number(orderValue)
      return !Number.isNaN(numValue) && numValue >= 0
    })

    // Sort by orderBy field (descending)
    processedRows.sort((r1: any, r2: any) => {
      const o1 = extractFieldValue(r1, fieldOrder)
      const o2 = extractFieldValue(r2, fieldOrder)

      if (o1 === undefined || o1 === null) return 1
      if (o2 === undefined || o2 === null) return -1

      const num1 = typeof o1 === 'number' ? o1 : Number(o1)
      const num2 = typeof o2 === 'number' ? o2 : Number(o2)

      if (Number.isNaN(num1) || Number.isNaN(num2)) return 0

      // Descending order (higher priority first)
      return num2 - num1
    })
  }

  // Build icon items
  const items: IconItem[] = processedRows.map((row: any) => {
    const code = extractFieldValue(row, fieldCode)
    const description = extractFieldValue(row, fieldName) ?? ''
    const iconName = extractFieldValue(row, fieldImage)
    const tooltip = extractFieldValue(row, fieldTooltip)

    return {
      code,
      description: String(description),
      icon: iconName ? String(iconName) : undefined,
      tooltip: tooltip ? String(tooltip) : undefined,
      originalValue: row
    }
  })

  return items
}

const parseFormatParams = (format: string): {
  name: string
  code?: string
  image?: string
  tooltip?: string
  orderBy?: string
} => {
  const params: Record<string, string> = {}

  const parts = format.split(';')
  for (const part of parts) {
    const eqIndex = part.indexOf('=')
    if (eqIndex > 0) {
      const key = part.substring(0, eqIndex).trim()
      const value = part.substring(eqIndex + 1).trim()
      params[key] = value
    }
  }

  const name = params['IconSubTableFormat'] || params['name'] || 'name'

  return {
    name,
    code: params['code'],
    image: params['image'],
    tooltip: params['tooltip'],
    orderBy: params['orderBy']
  }
}

const buildColumnFormatContext = (
  column: { value?: string; columnElementId?: string },
  context?: ColumnFormatContext,
): ColumnFormatContext | undefined => {
  if (!context) return undefined

  return {
    ...context,
    columnFieldName: context.columnFieldName ?? column?.value,
    columnElementId: context.columnElementId ?? column?.columnElementId,
  }
}

export const applyColumnFormat = (
  value: any,
  column: { format?: any; formatName?: any; type?: string | null; fields?: any; value?: string; columnElementId?: string },
  context?: ColumnFormatContext,
): { applied: boolean; value: any } => {
  const formatContext = buildColumnFormatContext(column, context)
  const unwrapFormatValue = (raw: any): string | null => {
    if (!raw) return null
    if (typeof raw === 'string') return raw
    if (typeof raw === 'object') {
      if (typeof raw.value === 'string') return raw.value
      if (typeof raw._text === 'string') return raw._text
      if (typeof raw.name === 'string') {
        const n = raw.name.trim()
        const t = typeof raw.type === 'string' ? raw.type.trim().toUpperCase() : ''
        if (/^(DATE|DATETIME|TIME|TIMESTAMP)$/i.test(n)) {
          if (!t || n.toUpperCase() === t) return null
        }
        return raw.name
      }
    }
    return null
  }

  const resolveFormatString = (): string | null => {
    const candidates = [
      column?.format,
      column?.format?.name,
      column?.format?.value,
      column?.format?._text,
      column?.formatName,
      column?.formatName?._text,
      column?.formatName?.value,
      column?.fields?.formatName?.value,
      column?.fields?.formatName?._text,
      column?.fields?.format?.value,
      column?.fields?.format?._text
    ]

    const shouldSkipBareTypeKeyword = (s: string): boolean => {
      const r = s.trim()
      const ct = String(column?.type ?? '').trim().toUpperCase()
      if (!ct) return false
      return /^(DATE|DATETIME|TIME|TIMESTAMP)$/i.test(r) && r.toUpperCase() === ct
    }

    for (const candidate of candidates) {
      const resolved = unwrapFormatValue(candidate)
      if (resolved && !shouldSkipBareTypeKeyword(resolved)) {
        return resolved
      }
    }

    return null
  }

  const peelOneLevel = (v: any): any => {
    if (v == null || typeof v !== 'object') return v
    if ('value' in v && (v as any).value !== undefined) return (v as any).value
    return v
  }

  const normalizedType = normalizeTableColumnType(column?.type)
  const isDateLikeType = isDateLikeTableColumnType(normalizedType)
  const valueForScalars = peelOneLevel(value)

  if (isTimezonelessDateWire(valueForScalars)) {
    const formatted = formatTimezonelessDateForDisplay(valueForScalars)
    if (formatted) {
      return { applied: true, value: formatted }
    }
  }

  const format = resolveFormatString()
  if (!format) {
    if (isDateLikeType) {
      const defaultMask = defaultSwingDateMaskForColumnType(normalizedType)
      if (defaultMask) {
        const formatted = formatDateByMask(valueForScalars, defaultMask)
        if (formatted !== null) {
          return { applied: true, value: formatted }
        }
      }
    }
    return { applied: false, value }
  }

  let trimmed = format.trim()
  if (!trimmed) {
    if (isDateLikeType) {
      const defaultMask = defaultSwingDateMaskForColumnType(normalizedType)
      if (defaultMask) {
        const formatted = formatDateByMask(valueForScalars, defaultMask)
        if (formatted !== null) {
          return { applied: true, value: formatted }
        }
      }
    }
    return { applied: false, value }
  }

  try {

    // IconSubTableFormat= contains the substring "SubTableFormat=" — match it first.
    const iconIdx = trimmed.indexOf('IconSubTableFormat=')
    if (iconIdx >= 0) {
      const spec = trimmed.slice(iconIdx)
      const params = parseFormatParams(spec)
      const items = formatIconSubTable(value, params)
      return { applied: true, value: { __iconItems: items } }
    }

    const subTableIdx = trimmed.indexOf('SubTableFormat=')
    if (subTableIdx >= 0) {
      const local = trimmed.slice(subTableIdx)
      const field = local.substring(local.indexOf('=') + 1)
      return { applied: true, value: formatSubTable(value, field) }
    }

    const subTableExtIdx = trimmed.indexOf('SubTableExtFormat=')
    if (subTableExtIdx >= 0) {
      const local = trimmed.slice(subTableExtIdx)
      const template = local.substring(local.indexOf('=') + 1)
      return { applied: true, value: formatSubTableExt(value, template) }
    }

    const subTableLbIdx = trimmed.indexOf('SubTableListBoxFormat=')
    if (subTableLbIdx >= 0) {
      const local = trimmed.slice(subTableLbIdx)
      const eqIdx = local.indexOf('=')
      const rest = local.substring(eqIdx + 1)
      const parts = rest.split(';')
      const codeFieldName = parts[0]?.trim() ?? ''
      const defaultPart = parts.find(part => part.startsWith('default='))
      const defaultValue = defaultPart ? defaultPart.substring('default='.length) : ''
      return {
        applied: true,
        value: formatSubTableListBox(value, codeFieldName, defaultValue, formatContext),
      }
    }

    if (trimmed.startsWith('FileSize')) {
      const formatted = formatFileSize(value)
      return formatted !== null ? { applied: true, value: formatted } : { applied: false, value }
    }

    if (trimmed.startsWith('MoneyFormat')) {
      const formatted = formatMoney(value)
      return formatted !== null ? { applied: true, value: formatted } : { applied: false, value }
    }

    if (isDateLikeType) {
      const formatted = formatDateByMask(valueForScalars, trimmed)
      if (formatted !== null) {
        return { applied: true, value: formatted }
      }
    }
  } catch (error) {
    console.warn('Failed to apply column format', { format: trimmed, value, error })
  }

  return { applied: false, value }
}

export type { IconItem }


